/**
 * Region download engine (spec 04, extended by spec 10): concurrency 4,
 * exponential backoff, resumable (remaining-tile manifest persisted every 100
 * tiles), tiles written into the Cache API bucket `region-<uuid>` that the SW
 * reads first.
 *
 * A region pack fetches from two sources in sequence — the base chart across
 * [zMin,zMax], then the routing depth tiles at z13 only — so offline
 * auto-routing (spec 09) works inside a downloaded region (spec 10 gap 2).
 * Both phases share one resumable queue; depth tiles are queued first so the
 * (LIFO) worker pop() drains the base chart first, matching "after chart
 * tiles" in the spec without a second progress manifest.
 */
import { tilesForBbox, AVG_TILE_BYTES, type Bbox } from '@boatnav/core';
import { regionCacheName } from './constants.js';
import { DEPTH_Z, DEPTH_TILES_PATH } from '../route/depthProvider.js';
import {
  putRegion,
  saveProgress,
  getProgress,
  clearProgress,
  type RegionMeta,
  type DownloadTile,
} from '../../lib/db.js';

const CONCURRENCY = 4;
const MAX_RETRIES = 3;
const CHECKPOINT_EVERY = 100;

export interface DownloadRequest {
  id: string;
  name: string;
  bbox: Bbox;
  zMin: number;
  zMax: number;
  token: string | null;
  /** Tile-proxy path for the active chart (e.g. '/base/enc'), so the cached
   *  region matches the layer shown on screen. */
  basePath: string;
  /** Depth-tile dataset edition (spec 10), for `?v=` on depth tile requests
   *  and stamped onto the resulting `RegionMeta`. Null if unresolved (e.g.
   *  starting a download while offline) — the region still downloads, just
   *  without a way to later detect it grew stale. */
  depthEdition: string | null;
}

export interface DownloadProgress {
  done: number;
  total: number;
  bytes: number;
}

export interface DownloadHandle {
  promise: Promise<{ complete: boolean }>;
  cancel: () => void;
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

function tilesFor(basePath: string, bbox: Bbox, zMin: number, zMax: number): DownloadTile[] {
  return tilesForBbox(bbox, zMin, zMax).map((t) => ({ ...t, basePath }));
}

function tileUrl(tile: DownloadTile, token: string | null, depthEdition: string | null): string {
  const params = new URLSearchParams();
  if (token) params.set('dl', token);
  if (tile.basePath === DEPTH_TILES_PATH && depthEdition) params.set('v', depthEdition);
  const qs = params.toString();
  return `${tile.basePath}/${tile.z}/${tile.x}/${tile.y}.png${qs ? `?${qs}` : ''}`;
}

async function fetchTile(url: string): Promise<Response> {
  let lastErr: unknown;
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      const res = await fetch(url);
      // Non-image 200s (e.g. the SPA fallback) must not land in a region bucket.
      if (res.ok && (res.headers.get('content-type') ?? '').startsWith('image/')) return res;
      lastErr = new Error(`HTTP ${res.status} ${res.headers.get('content-type') ?? ''}`);
    } catch (err) {
      lastErr = err;
    }
    await sleep(300 * 2 ** attempt);
  }
  throw lastErr instanceof Error ? lastErr : new Error('tile fetch failed');
}

export function startRegionDownload(
  req: DownloadRequest,
  onProgress: (p: DownloadProgress) => void,
): DownloadHandle {
  let cancelled = false;

  const promise = (async (): Promise<{ complete: boolean }> => {
    const saved = await getProgress(req.id);
    const queue: DownloadTile[] = saved
      ? saved.remaining.slice()
      : [
          // Depth tiles queued first so `.pop()` (LIFO) drains the base chart
          // to completion before touching the depth phase (spec 10).
          ...tilesFor(DEPTH_TILES_PATH, req.bbox, DEPTH_Z, DEPTH_Z),
          ...tilesFor(req.basePath, req.bbox, req.zMin, req.zMax),
        ];
    const total = saved?.total ?? queue.length;
    let done = saved?.done ?? 0;
    const failed: DownloadTile[] = [];

    const cache = await caches.open(regionCacheName(req.id));

    const checkpoint = async (): Promise<void> => {
      await saveProgress({ regionId: req.id, remaining: [...queue, ...failed], done, total });
    };

    const worker = async (): Promise<void> => {
      while (!cancelled) {
        const tile = queue.pop();
        if (!tile) return;
        const url = tileUrl(tile, req.token, req.depthEdition);
        try {
          const res = await fetchTile(url);
          await cache.put(new Request(url), res);
          done += 1;
        } catch {
          failed.push(tile);
        }
        if (done % CHECKPOINT_EVERY === 0) await checkpoint();
        onProgress({ done, total, bytes: done * AVG_TILE_BYTES });
      }
    };

    await Promise.all(Array.from({ length: CONCURRENCY }, worker));

    if (cancelled) {
      await checkpoint();
      return { complete: false };
    }

    // One retry pass over tiles that failed their attempts.
    if (failed.length) {
      const retry = failed.splice(0);
      for (const tile of retry) {
        if (cancelled) break;
        const url = tileUrl(tile, req.token, req.depthEdition);
        try {
          await cache.put(new Request(url), await fetchTile(url));
          done += 1;
        } catch {
          failed.push(tile);
        }
        onProgress({ done, total, bytes: done * AVG_TILE_BYTES });
      }
    }

    const complete = failed.length === 0 && !cancelled;
    const region: RegionMeta = {
      id: req.id,
      name: req.name,
      bbox: req.bbox,
      zMin: req.zMin,
      zMax: req.zMax,
      tileCount: done,
      bytes: done * AVG_TILE_BYTES,
      createdAt: Date.now(),
      complete,
      depthEdition: req.depthEdition,
    };
    await putRegion(region);

    if (complete) {
      await clearProgress(req.id);
      await requestPersistentStorage();
    } else {
      await checkpoint();
    }
    return { complete };
  })();

  return { promise, cancel: () => (cancelled = true) };
}

/** Ask the browser to exempt our storage from eviction (spec 04). */
export async function requestPersistentStorage(): Promise<boolean> {
  try {
    if (navigator.storage?.persist) return await navigator.storage.persist();
  } catch {
    /* not supported */
  }
  return false;
}

/** Drop a region's tiles and manifest. */
export async function deleteRegionCache(id: string): Promise<void> {
  await caches.delete(regionCacheName(id));
}
